import {
    AnalyticsActivity,
    AnalyticsPeople,
    PeopleCounts,
    ActivityCounts,
    UserActivityCounts,
} from 'src/domain/analytics';
import { DEFAULT_MINUTES_PER_MESSAGE, estimatedHours } from './timeEstimate';

// Quote every cell and neutralize spreadsheet formulas in organization names.
export const csvCell = (value: string | number): string => {
    const text = String(value);
    const safe =
        typeof value === 'string' && /^[\s]*[=+@-]/.test(text)
            ? "'" + text
            : text;
    return '"' + safe.replace(/"/g, '""') + '"';
};

const ALL_RELATIVE_METRICS = new Set([
    'relativeLinks',
    'familyRelatives',
    'externalRelatives',
    'coveredResidents.30d',
    'residentCoveragePercent.30d',
]);

/** Employee and relative rows state which population they count; other rows have no cohort. */
const cohort = (category: string, metric: string): string => {
    if (metric === 'totalEmployees') return 'all employees';
    if (
        metric.startsWith('employees.') ||
        category === 'full-time employee coverage'
    )
        return 'full time';
    if (
        metric === 'mainContacts' ||
        metric === 'residentsWithMainContact' ||
        metric.startsWith('mainContacts.') ||
        metric.startsWith('mainContactAccounts.') ||
        category === 'main contact coverage' ||
        category === 'active main contact coverage'
    )
        return 'main contacts';
    if (
        metric.startsWith('relatives.') ||
        ALL_RELATIVE_METRICS.has(metric) ||
        category === 'resident links' ||
        category === 'active relative coverage'
    )
        return 'all relatives';
    return '';
};

export const buildAnalyticsCsv = (
    people: AnalyticsPeople,
    activity: AnalyticsActivity,
    customMinutes: number | null = null,
): string => {
    const minutes = customMinutes ?? DEFAULT_MINUTES_PER_MESSAGE;
    const rows: (string | number)[][] = [
        [
            'Scope',
            'Organization ID',
            'Organization',
            'Home type',
            'Category',
            'Metric',
            'Value',
            'Period',
            'Snapshot time',
            'Time zone',
        ],
    ];
    const add = (
        id: string,
        name: string,
        type: string,
        category: string,
        metric: string,
        value: number | null,
        period: string,
        asOf: string,
    ) =>
        rows.push([
            id ? 'organization' : 'total',
            id,
            name,
            type,
            category,
            metric,
            value ?? '',
            period,
            asOf,
            people.timeZone,
        ]);
    const current = (
        id: string,
        name: string,
        type: string,
        counts: PeopleCounts,
        usage?: UserActivityCounts,
    ) => {
        const row = (category: string, metric: string, value: number | null) =>
            add(
                id,
                name,
                type,
                category,
                metric,
                value,
                'current',
                people.asOf,
            );
        [
            'residents',
            'totalEmployees',
            'mainContacts',
            'residentsWithMainContact',
            'relativeLinks',
            'guardians',
            'mainContactLinks',
            'guardianLinks',
            'organizationAdmins',
            'employeeManagers',
            'broadcasters',
            'familyRelatives',
            'externalRelatives',
        ].forEach(key =>
            row(
                'people',
                key,
                key === 'totalEmployees'
                    ? counts.totalEmployees ?? counts.employees.total
                    : (counts[key as keyof PeopleCounts] as
                          | number
                          | undefined) ?? null,
            ),
        );
        (['employees', 'relatives', 'mainContactAccounts'] as const).forEach(
            role =>
                Object.entries(counts[role] ?? {}).forEach(([key, value]) =>
                    row('people', `${role}.${key}`, value),
                ),
        );
        counts.residentCoverage.forEach((value, index) =>
            row(
                'resident links',
                `${index === 3 ? '3+' : index} relatives`,
                value,
            ),
        );
        if (usage) {
            if (usage.employeeCoverage) {
                Object.entries(usage.employeeCoverage).forEach(
                    ([status, count]) =>
                        row(
                            'full-time employee coverage',
                            status,
                            usage.trackingSince ? count : null,
                        ),
                );
            }
            (['employees', 'relatives', 'mainContacts'] as const).forEach(
                role =>
                    Object.entries(usage[role] ?? {}).forEach(([key, value]) =>
                        add(
                            id,
                            name,
                            type,
                            'observed activity',
                            `${role}.${key}`,
                            usage.trackingSince ? value : null,
                            `rolling ${
                                key === 'day' ? 1 : key === 'week' ? 7 : 30
                            } days`,
                            people.asOf,
                        ),
                    ),
            );
            row(
                'observed activity',
                'coveredResidents.30d',
                usage.trackingSince ? usage.coveredResidents : null,
            );
            row(
                'observed activity',
                'residentCoveragePercent.30d',
                usage.trackingSince && counts.residents
                    ? Math.round(
                          (usage.coveredResidents / counts.residents) * 1000,
                      ) / 10
                    : null,
            );
            usage.residentCoverage.forEach((value, index) =>
                row(
                    'active relative coverage',
                    `${index === 3 ? '3+' : index} relatives.30d`,
                    usage.trackingSince ? value : null,
                ),
            );
            usage.activeMainContactCoverage?.forEach((value, index) =>
                row(
                    'active main contact coverage',
                    `${index === 3 ? '3+' : index} main contacts.30d`,
                    usage.trackingSince ? value : null,
                ),
            );
            if (usage.mainContactCoverage) {
                Object.entries(usage.mainContactCoverage).forEach(
                    ([status, count]) =>
                        row(
                            'main contact coverage',
                            status,
                            usage.trackingSince ? count : null,
                        ),
                );
                row(
                    'main contact coverage',
                    'noActiveMainContact',
                    usage.trackingSince
                        ? counts.residents - usage.mainContactCoverage.active
                        : null,
                );
                row(
                    'main contact coverage',
                    'activePercent.30d',
                    usage.trackingSince && counts.residents
                        ? Math.round(
                              (usage.mainContactCoverage.active /
                                  counts.residents) *
                                  1000,
                          ) / 10
                        : null,
                );
            }
            rows.push([
                id ? 'organization' : 'total',
                id,
                name,
                type,
                'observed activity',
                'trackingSince',
                usage.trackingSince ?? '',
                'current',
                people.asOf,
                people.timeZone,
            ]);
        }
    };
    const period = (
        id: string,
        name: string,
        type: string,
        counts: ActivityCounts,
    ) => {
        (['messages', 'responses', 'calendar'] as const).forEach(category =>
            Object.entries(counts[category])
                .filter(
                    ([key]) =>
                        category !== 'responses' ||
                        ![
                            'eligible24h',
                            'unanswered24h',
                            'eligible72h',
                            'unanswered72h',
                        ].includes(key),
                )
                .filter(([key]) => key !== 'eventsByType')
                .forEach(([key, value]) =>
                    add(
                        id,
                        name,
                        type,
                        category,
                        key,
                        value,
                        activity.month,
                        activity.asOf,
                    ),
                ),
        );
        Object.entries(counts.calendar.eventsByType ?? {}).forEach(
            ([eventType, count]) =>
                add(
                    id,
                    name,
                    type,
                    'calendar',
                    `eventsByType.${eventType}`,
                    count,
                    activity.month,
                    activity.asOf,
                ),
        );
        add(
            id,
            name,
            type,
            'estimate',
            'timeFreedHours',
            estimatedHours(counts.messages, minutes),
            activity.month,
            activity.asOf,
        );
        add(
            id,
            name,
            type,
            'estimate',
            'minutesPerOriginalMessage',
            minutes,
            activity.month,
            activity.asOf,
        );
        add(
            id,
            name,
            type,
            'estimate',
            'usesDefaultAssumption',
            customMinutes === null ? 1 : 0,
            activity.month,
            activity.asOf,
        );
        ([24, 48, 72] as const).forEach(hours => {
            const eligible = counts.responses[`answeredEligible${hours}h`];
            const replied = counts.responses[`repliedWithin${hours}h`];
            add(
                id,
                name,
                type,
                'responses',
                `repliedWithin${hours}hPercent`,
                eligible && replied !== undefined
                    ? Math.round((replied / eligible) * 1000) / 10
                    : null,
                activity.month,
                activity.asOf,
            );
        });
    };
    current('', 'All selected organizations', '', people.totals, people.usage);
    add(
        '',
        'All selected organizations',
        '',
        'people',
        'organizations',
        people.organizations.length,
        'current',
        people.asOf,
    );
    period('', 'All selected organizations', '', activity.totals);
    add(
        '',
        'All organizations',
        '',
        'people',
        'globalMunduAdministrators',
        people.globalAdminCount,
        'unfiltered',
        people.asOf,
    );
    people.organizations.forEach(org =>
        current(org.id, org.name, org.type ?? '', org.people, org.usage),
    );
    activity.organizations.forEach(org =>
        period(org.id, org.name, org.type ?? '', org),
    );
    activity.trend.forEach(month =>
        Object.entries(month).forEach(([metric, value]) => {
            if (['organization', 'relative', 'other'].includes(metric))
                add(
                    '',
                    'All selected organizations',
                    '',
                    'monthly originals',
                    metric,
                    value as number,
                    month.month,
                    activity.asOf,
                );
        }),
    );
    const department = [
        people.department?.id ?? '',
        people.department?.organizationId ?? '',
        people.department?.name ?? '',
    ];
    return (
        '\uFEFF' +
        rows
            .map((row, index) =>
                [
                    ...row,
                    ...(index === 0
                        ? [
                              'Department ID',
                              'Department organization ID',
                              'Department',
                              'Cohort',
                          ]
                        : [
                              ...department,
                              cohort(String(row[4]), String(row[5])),
                          ]),
                ]
                    .map(csvCell)
                    .join(','),
            )
            .join('\r\n')
    );
};

export const downloadAnalyticsCsv = (
    people: AnalyticsPeople,
    activity: AnalyticsActivity,
    customMinutes: number | null = null,
): void => {
    const url = URL.createObjectURL(
        new Blob([buildAnalyticsCsv(people, activity, customMinutes)], {
            type: 'text/csv;charset=utf-8',
        }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `mundu-analytics-${activity.month}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};
